'use strict';

import React from 'react';
import { useState, useEffect } from 'react';
import { Table, Button, Modal, Row, Col, Form, Popover, OverlayTrigger } from 'react-bootstrap';

import useRequest from '../helper/useRequest';
import { toast } from 'react-toastify';
import Alert from '../helper/Alert';

import { customFilterFunction } from '../helper/CustomFilter';
import TableHeader from '../helper/TableHeader';

import Loader from '../helper/Loader';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table'

const columnHelper = createColumnHelper();

function TemplateModal({ modal, setModal, template, updateTemplate }) {
    const [message, setMessage] = useState("");

    const [subject, setSubject] = useState("");
    const [text, setText] = useState("");
    const [placeholder, setPlaceholder] = useState([]);

    const closeModal = () => {
        setModal(false);
    }

    const onSubmit = (template) => {
        if (subject.trim().length == 0)
            return setMessage("Sie müssen einen Betreff angeben.");

        if (text.trim().length == 0)
            return setMessage("Sie müssen einen Text angeben.");

        setMessage("");
        updateTemplate(template, subject, text);
    }

    useEffect(() => {
        if (!template.Subject) return;

        setSubject(template.Subject);
        setText(template.Text);

        if (template.Placeholder != null) {
            try {
                let json = JSON.parse(template.Placeholder);
                setPlaceholder(json);
            }
            catch (ex) {
                console.error(ex);
                toast.error("Placeholder sind defekt.");
            }
        }
    }, [template])

    const placeholderPopover = (
        <Popover id="placeholder-popover" style={{ width: "400px" }}>
            <Popover.Header as="h3">Platzhalter</Popover.Header>
            <Popover.Body>
                {placeholder.map((item, index) => {
                    return (
                        <Row key={index}>
                            <Col sm={5}>{item.name}</Col>
                            <Col sm={7}>{item.placeholder}</Col>
                        </Row>
                    )
                })}
            </Popover.Body>
        </Popover>
    );

    return (
        <Modal size="lg" show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Vorlage bearbeiten</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {message && <Alert type="error" message={message} />}
                <Row className="mb-2">
                    <Col sm={2}>
                        <Form.Label>Vorlage</Form.Label>
                    </Col>
                    <Col sm={10}>
                        {template.Name}
                    </Col>
                </Row>
                <Row>
                    <Col sm={2}>
                        <Form.Label htmlFor="subject">Betreff</Form.Label>
                    </Col>
                    <Col sm={10}>
                        <Form.Control id="subject" value={subject} onChange={(e) => { return setSubject(e.target.value); }} size="sm" />
                    </Col>
                </Row>
                
                <Row className="align-items-center">
                    <Col sm={6}>
                        <Form.Label htmlFor="text">Text</Form.Label>
                    </Col>
                    <Col sm={6} className="text-end">
                        {placeholder.length > 0 && (
                            <OverlayTrigger trigger={["click", "hover", "focus"]} placement="right" overlay={placeholderPopover}>
                                <i className="fa-solid fa-circle-question"></i>
                            </OverlayTrigger>
                        )}
                    </Col>
                    <Col sm={12}>
                        <Form.Control id="text" as="textarea" rows={10} value={text} onChange={(e) => setText(e.target.value)} size="sm" />
                    </Col>
                </Row>   
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Abbrechen
                </Button>
                <Button variant="primary" onClick={() => onSubmit(template)}>
                    Speichern
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

function Templates() {
    const { request } = useRequest();

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);

    const [template, setTemplate] = useState({});
    const [templates, setTemplates] = useState(false);
    const [modal, setModal] = useState(false);
    const [sendingTestmail, setSendingTestmail] = useState(false);

    const editTemplate = (template) => {
        setTemplate(template);
        setModal(true);
    };

    const updateTemplate = async (template, subject, text) => {
        try {
            let data = { TemplateId: template.TemplateId, Subject: subject, Text: text };
            let result = await request("/Operator/SaveTemplate", data);
            let json = result.data;
            if (json.r == 1) {
                setTemplates((items) => {
                    return items.map((item) => {
                        if (item.TemplateId == template.TemplateId) {
                            item.Subject = subject;
                            item.Text = text;
                            return item;
                        }
                        else {
                            return item;
                        }
                    });
                });
                setModal(false);
                toast.success("Vorlage wurde erfolgreich gespeichert.");
            }
            else {
                toast.error(json.message);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
    }

    const sendMail = async (template) => {
        setSendingTestmail(true);
        try {
            let result = await request("/Operator/TestTemplate", template);
            let json = result.data;
            if (json.r == 1) {
                toast.success("Test E-Mail wurde erfolgreich an Ihre E-Mail-Adresse verschickt.");
            }
            else {
                toast.error(json.message);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
        setSendingTestmail(false);
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await request("/Operator/GetTemplates");
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten.");
                    }
                }
                else {
                    setTemplates(json.templates);
                }
            }
            catch (ex) {
                console.error(ex);
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        };
        fetchData();
    }, []);

    const columns = [
        columnHelper.accessor('Name', {
            header: () => <span>Name</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
        }),
        columnHelper.accessor('Subject', {
            header: () => <span>Betreff</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
        }),
        columnHelper.accessor('TemplateID', {
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end">
                    <Button disabled={sendingTestmail} className="me-1" size="sm" variant="secondary" onClick={() => sendMail(info.row.original)}>Testmail</Button>
                    <Button size="sm" variant="primary" onClick={() => { editTemplate(info.row.original) }}>Bearbeiten</Button>
                </div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: templates || [],
        columns,
        state: {
            sorting,
            columnFilters
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    if (templates === false) return <Loader />

    return (
        <>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            <TemplateModal modal={modal} setModal={setModal} template={template} updateTemplate={updateTemplate} />
        </>
    );
}

export default Templates;